import React from "react";

import { getImage, ImageType } from "../../../../helpers/imageHelper";
import { useString, useStrings } from "../../../../localization/localizationHelpers";

import * as styles from "./PartnerSuccessSection.module.css";

const strayFawnBoxArt = getImage("landing_strayfawn_boxart", ImageType.Png);
const strayFawnBackground = getImage("landing_strayfawn_background", ImageType.Png);
const strayFawnMobileBackground = getImage("landing_strayfawn_background_mobile", ImageType.Png);
const voidInteractiveBoxArt = getImage("landing_voidinteractive_boxart", ImageType.Png);
const voidInteractiveBackground = getImage("landing_voidinteractive_background", ImageType.Png);
const voidInteractiveMobileBackground = getImage(
  "landing_voidinteractive_background_mobile",
  ImageType.Png
);
const cardGlowPath = [
  "M0.334961 121.999C0.468294 105.471 0.594961 88.9423 0.714961 72.414",
  "C0.832129 56.2757 0.942942 40.1374 1.0474 23.999",
  "C0.833261 11.5895 12.0621 0.564915 24.335 0.94392",
  "C144.385 1.64732 264.435 1.99902 384.485 1.99902",
  "C533.24 1.99902 681.995 1.45902 830.75 0.379023",
  "C847.278 0.259023 863.807 0.132357 880.335 -0.000976562",
  "C863.807 -0.13431 847.278 -0.260977 830.75 -0.380977",
  "C681.995 -1.46098 533.24 -2.00098 384.485 -2.00098",
  "C264.435 -2.00098 144.385 -1.64928 24.335 -0.945873",
  "C11.0401 -1.18855 -0.785021 10.8408 -0.377477 23.999",
  "C-0.27302 40.1374 -0.162207 56.2757 -0.0450391 72.414",
  "C0.0749609 88.9423 0.201628 105.471 0.334961 121.999Z",
].join("");

type CaseStudyProps = Readonly<{
  className: string;
  backgroundImage: string;
  mobileBackgroundImage: string;
  boxArt: string;
  studio: string;
  game: string;
  description: string;
}>;

const CaseStudyTile = ({
  className,
  backgroundImage,
  mobileBackgroundImage,
  boxArt,
  studio,
  game,
  description,
}: CaseStudyProps) => {
  const boxArtAlt = useString("landingSuccessBoxArtAlt", game);
  const keyArtAlt = useString("landingSuccessCaseImageAlt", game);

  return (
    <div className={className}>
      <picture>
        <source media="(max-width: 1024px)" srcSet={mobileBackgroundImage} />
        <img className={styles.caseKeyArt} src={backgroundImage} alt={keyArtAlt} />
      </picture>
      <div className={styles.caseContent}>
        <div className={styles.caseHeader}>
          <img className={styles.caseLogo} src={boxArt} alt={boxArtAlt} />
          <div className={styles.caseHeaderText}>
            <span className={styles.caseStudio}>{studio}</span>
            <span className={styles.caseGame}>{game}</span>
          </div>
        </div>
        <div className={styles.caseFooter}>
          <span className={styles.statDescription}>{description}</span>
        </div>
      </div>
    </div>
  );
};

export const PartnerSuccessSection = () => {
  const [
    mauStat,
    mauLabel,
    mauDescription,
    paidStat,
    paidLabel,
    paidDescription,
    voidInteractiveStudio,
    voidInteractiveGame,
    voidInteractiveDescription,
    strayFawnStudio,
    strayFawnGame,
    strayFawnDescription,
  ] = useStrings(
    "landingSuccessMauStat",
    "landingSuccessMauLabel",
    "landingSuccessMauDescription",
    "landingSuccessPaidStat",
    "landingSuccessPaidLabel",
    "landingSuccessPaidDescription",
    "landingSuccessVoidInteractiveStudio",
    "landingSuccessVoidInteractiveGame",
    "landingSuccessVoidInteractiveDescription",
    "landingSuccessStrayFawnStudio",
    "landingSuccessStrayFawnGame",
    "landingSuccessStrayFawnDescription"
  );

  return (
    <section className={styles.section}>
      <div className={styles.card}>
        <div className={styles.cardGlowClip} aria-hidden="true">
          <svg
            className={styles.cardGlow}
            viewBox="0 0 981 222"
            fill="none"
            preserveAspectRatio="none"
          >
            <g filter="url(#partner-success-card-glow)">
              <path d={cardGlowPath} fill="#6FDC94" />
            </g>
            <defs>
              <filter
                id="partner-success-card-glow"
                x="-100.388"
                y="-102.001"
                width="1080.72"
                height="324"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset />
                <feGaussianBlur stdDeviation="50" />
                <feComposite in2="hardAlpha" operator="out" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0.435294 0 0 0 0 0.862745 0 0 0 0 0.580392 0 0 0 1 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_15_14321"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect1_dropShadow_15_14321"
                  result="shape"
                />
              </filter>
            </defs>
          </svg>
        </div>
        <div className={styles.grid}>
          <div className={styles.statsRow}>
            <div className={styles.statTile}>
              <span className={styles.statNumber}>{mauStat}</span>
              <span className={styles.statLabel}>{mauLabel}</span>
              <span className={styles.statDescription}>{mauDescription}</span>
            </div>
            <div className={styles.statTile}>
              <span className={styles.statNumber}>{paidStat}</span>
              <span className={styles.statLabel}>{paidLabel}</span>
              <span className={styles.statDescription}>{paidDescription}</span>
            </div>
          </div>
          <CaseStudyTile
            className={styles.caseTileWide}
            backgroundImage={voidInteractiveBackground}
            mobileBackgroundImage={voidInteractiveMobileBackground}
            boxArt={voidInteractiveBoxArt}
            studio={voidInteractiveStudio}
            game={voidInteractiveGame}
            description={voidInteractiveDescription}
          />
          <CaseStudyTile
            className={styles.caseTileTall}
            backgroundImage={strayFawnBackground}
            mobileBackgroundImage={strayFawnMobileBackground}
            boxArt={strayFawnBoxArt}
            studio={strayFawnStudio}
            game={strayFawnGame}
            description={strayFawnDescription}
          />
        </div>
      </div>
    </section>
  );
};
